import { useOktaAuth } from '@okta/okta-react';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { styled, Grid, SvgIcon, Typography } from '@mui/material';

import type { RootState } from 'store';

import { ReactComponent as NonPatientIcon } from 'img/site/site_login_select_non_patient.svg';
import { ReactComponent as PatientIcon } from 'img/site/site_login_select_patient.svg';

import { AuthActions } from 'common/auth/actions';
import { CILogger } from 'common/log/log.provider';
import { LoadingOverlay } from 'common/utils/loading';
import { CIButtonGroup, CITertiaryButton } from 'common/viewcomp/forms/buttons';
import { guardedOktaSignInWithRedirect } from 'module/auth/okta/utils/oktaUtils';

export const UserTypeSelection = (): ReactElement => {
    const { t } = useTranslation('auth');
    const ssoLoading = useSelector((state: RootState) => state.auth.ssoLoading);
    const { oktaAuth } = useOktaAuth();
    const dispatch = useDispatch();
    const history = useHistory();

    const [isLoading, setIsLoading] = useState(false);

    const handlePatientSelection = (): void => {
        history.push('/patientLogin');
    };

    const handleNonPatientSelection = async () => {
        dispatch(AuthActions.setSSOLoading(true));
        setIsLoading(true);
        try {
            await guardedOktaSignInWithRedirect(oktaAuth);
        } catch (e) {
            dispatch(AuthActions.setSSOLoading(false));
            setIsLoading(false);
            CILogger.getInstance().error('Error initiating Okta redirect:', e);
        }
    };

    return (
        <LoadingOverlay isLoading={ssoLoading || isLoading} clickThrough={!!ssoLoading}>
            <SelectUserContainer>
                <Typography variant="h6">{t('select_user.header')}</Typography>
                <CIButtonGroup>
                    <Grid item sm={6} md={6}>
                        <SelectUserButton
                            data-testid="select-user-patient"
                            id="select-user-patient"
                            onClick={handlePatientSelection}
                        >
                            <UserButton>
                                <UserButtonBackground>
                                    <SvgIcon viewBox="0 0 32 32" component={PatientIcon} />
                                </UserButtonBackground>
                                <UserButtonText>{t('select_user.patient')}</UserButtonText>
                            </UserButton>
                        </SelectUserButton>
                    </Grid>
                    <Grid item sm={6} md={6}>
                        <SelectUserButton
                            id="select-user-non-patient"
                            data-testid="select-user-non-patient"
                            variant="outlined"
                            onClick={handleNonPatientSelection}
                        >
                            <UserButton>
                                <UserButtonBackground>
                                    <SvgIcon viewBox="0 0 32 32" component={NonPatientIcon} />
                                </UserButtonBackground>

                                <UserButtonText>{t('select_user.non_patient')}</UserButtonText>
                            </UserButton>
                        </SelectUserButton>
                    </Grid>
                </CIButtonGroup>
            </SelectUserContainer>
        </LoadingOverlay>
    );
};

const SelectUserContainer = styled('div')(() => ({
    width: '400px',
    minHeight: '250px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
}));

const UserButton = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
}));

const UserButtonBackground = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '90px',
    height: '90px',
    borderRadius: '50%',
    background: theme.ciPalette.background.powderGray,
}));

const UserButtonText = styled('div')(({ theme }) => ({
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '16px',
    lineHeight: '29px',
    marginTop: '21px',
    color: theme.ciPalette.brand.blue,
}));

const SelectUserButton = styled(CITertiaryButton)(({ theme }) => ({
    height: '190px',
    width: '190px',
    textTransform: 'none',
    border: `1px solid ${theme.ciPalette.border.lightGray}`,
}));
